import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { checkoutShippingOrderNotesEntryStyles } from "@insite/content-library/Widgets/CheckoutShipping/CheckoutShippingOrderNotesEntry";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Grid from "@insite/mobius/Icons/Grid";
import List from "@insite/mobius/Icons/List";
import Table from "@insite/mobius/Icons/Table";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getProductListDataView(state).value,
    view: state.pages.productList.view,
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    iconWrapper?: InjectableCss;
    listViewClickable?: ClickablePresentationProps;
    listViewIcon?: IconPresentationProps;
    gridViewClickable?: ClickablePresentationProps;
    gridViewIcon?: IconPresentationProps;
    tableViewClickable?: ClickablePresentationProps;
    tableViewIcon?: IconPresentationProps;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-top: 5px;
            float: right;
        `,
    },
    iconWrapper: {
        css: css`
            position: relative;
            display: inline-block;
            .hover-text {
                position: absolute;
                top: -25px; /* Adjust this as needed */
                left: 30%;
                transform: translateX(-50%);
                visibility: hidden;
                opacity: 0;
                background-color: black;
                color: white;
                padding: 5px;
                border-radius: 3px;
                font-size: 12px;
                transition: opacity 0.3s ease;
            }

            &:hover .hover-text {
                visibility: visible;
                opacity: 1;
                width: max-content;
                cursor: pointer;
            }
        `,
    },
    listViewClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    gridViewClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    tableViewClickable: {
        css: css`
            display: none;
        `,
    },
    listViewIcon: {
        src: List,
        size: 30,
    },
    gridViewIcon: {
        src: Grid,
        size: 30,
    },
    tableViewIcon: {
        src: Table,
        size: 26,
    },
};

const styles = viewSelectStyles;

class ProductListViewSelect extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const view = getCookie("ProductList-View");
        if (view && this.props.view !== view) {
            this.props.setView({ view: view as ProductListViewType });
        }
    }

    render() {
        const { loaded, view, setView } = this.props;

        if (!loaded) {
            return null;
        }

        const clickListViewHandler = () => {
            changeView("List");
        };

        const clickGridViewHandler = () => {
            changeView("Grid");
        };

        const changeView = (view: ProductListViewType) => {
            setCookie("ProductList-View", view);
            setView({ view });
        };

        if (!this.props.pagination || !this.props.pagination.totalItemCount) {
            return null;
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.iconWrapper}>
                    <Clickable
                        data-test-selector="viewSelectList"
                        {...styles.listViewClickable}
                        onClick={clickListViewHandler}
                    >
                        <Icon {...styles.listViewIcon} color={view === "List" ? "primary" : undefined} />
                    </Clickable>
                    <div className="icon-container">
                        <span className="hover-text">List view</span>
                    </div>
                </StyledWrapper>
                <StyledWrapper {...styles.iconWrapper}>
                    <Clickable
                        data-test-selector="viewSelectGrid"
                        {...styles.gridViewClickable}
                        onClick={clickGridViewHandler}
                    >
                        <Icon {...styles.gridViewIcon} color={view === "Grid" ? "primary" : undefined} />
                    </Clickable>
                    <div className="icon-container">
                        <span className="hover-text">Grid View</span>
                    </div>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductListViewSelect);
